import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import BixButton from '$/components/global/bixButton';
import { updateDraft } from '$/store/draft/actions';
import Loading from '$/components/loading/Loading';
import { GridFactory } from '$/factory/GridFactory';
import { AgGridReact } from 'ag-grid-react';
import { ColumnApi, GridApi } from 'ag-grid-enterprise';
import './CompanyLoanDetails.scss';

export interface ICompanyLoanDetailsProps {
  draftCompany?: Bixby.Models.DraftCompany;
  currentCompany?: Bixby.Models.AdminCompany;
  isEditing?: boolean;
  isLoading: boolean;
  discardDraft?: boolean;
  dispatchUpdateDraftCompany: (companyId: string, updatedCompany: Bixby.Models.JsonPatchOperation[]) => void;
}

const CompanyLoanDetails: React.FC<ICompanyLoanDetailsProps> = props => {
  const { draftCompany, currentCompany, isEditing, discardDraft, dispatchUpdateDraftCompany, isLoading } = props;
  const [loanRows, setLoanRows] = useState(undefined as Bixby.Models.DraftCompanyLoan[] | undefined);
  const [gridApi, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);

  useEffect(() => {
    if (isEditing && draftCompany && draftCompany.loanDetails) {
      createLoanRows(draftCompany.loanDetails);
    } else if ((!isEditing || discardDraft) && currentCompany && currentCompany.loanDetails) {
      createLoanRows(currentCompany.loanDetails);
    }
  }, [isEditing, discardDraft, draftCompany, currentCompany]);

  const createLoanRows = (companyLoanDetails: Bixby.Models.DraftCompanyLoanDetails) => {
    if (companyLoanDetails.loans) {
      const loans: Bixby.Models.DraftCompanyLoan[] = companyLoanDetails.loans.map<Bixby.Models.DraftCompanyLoan>(r =>
        Object.assign({}, r)
      );
      loans.forEach(r => {
        const creditor = companyLoanDetails.creditors.find(c => c.debtType === r.debtType);
        if (creditor) {
          r.name = creditor.name;
        }
        if (!r.covenantLite) {
          r.covenantLite = 'NM' as any;
        }
        r.maturity = r.maturity ? (Bixby.Utils.utcDateToString(r.maturity) as any) : Bixby.Constants.MATURITY_EMPTY;
      });

      const sortOrderValues: { [key: string]: number } = {
        ABL: 0,
        'Priority Debt': 1,
        Revolver: 2,
        '1st Lien - 1': 3,
        '1st Lien - 2': 4,
        '1st Lien - 3': 5,
        '1st Lien - 4': 6,
        '1st Lien - 5': 7,
        '2nd Lien - 1': 8,
        '2nd Lien - 2': 9,
        '2nd Lien - 3': 10,
        '2nd Lien - 4': 11,
        '2nd Lien - 5': 12,
        'Other debt - 1': 13,
        'Other debt - 2': 14,
        'Other debt - 3': 15,
        'Other debt - 4': 16,
        'Other debt - 5': 17,
      };
      const compareLoanRows = (a: Bixby.Models.DraftCompanyLoan, b: Bixby.Models.DraftCompanyLoan) => {
        const aSort = a.debtType ? sortOrderValues[a.debtType] : null;
        const bSort = b.debtType ? sortOrderValues[b.debtType] : null;

        if (aSort === null && bSort === null) {
          return 0;
        } else if (aSort === null) {
          return -1;
        } else if (bSort === null) {
          return 1;
        } else if (aSort < bSort) {
          return -1;
        } else if (aSort > bSort) {
          return 1;
        } else {
          return 0;
        }
      };
      setLoanRows(loans.sort(compareLoanRows));
    } else {
      setLoanRows([]);
    }
  };

  const handleSaveDraft = () => {
    if (draftCompany && loanRows) {
      const parsed = GridFactory.convertTableToCompanyLoanDetails(loanRows);
      const updateValues: Bixby.Models.JsonPatchOperation[] = [
        {
          op: 'replace',
          path: `/loanDetails/loans`,
          value: parsed.loans,
          from: '',
        },
        {
          op: 'replace',
          path: `/loanDetails/creditors`,
          value: parsed.creditors,
          from: '',
        },
      ];

      dispatchUpdateDraftCompany(draftCompany.id, updateValues);
    }
  };

  const onCellValueChanged = () => {
    const rowData: Bixby.Models.DraftCompanyLoan[] = [];
    if (gridApi) {
      gridApi.forEachNode(node => rowData.push(node.data));
    }
    setLoanRows(rowData);
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  return isLoading || !loanRows ? (
    <Loading inline />
  ) : (
    <div className="admin-profile-section bottom">
      <React.Fragment>
        <section>
          <h3>Loan Details</h3>
          {isEditing && (
            <div>
              <div className="loan-details-data-grid">
                <div className="grid_container">
                  <div className="grid ag-theme-alpine">
                    <AgGridReact
                      getContextMenuItems={getContextMenuItems}
                      defaultColDef={{
                        flex: 1,
                        minWidth: 100,
                        sortable: true,
                        resizable: true,
                        lockPinned: true,
                        cellClass: 'align-center',
                      }}
                      onGridReady={onGridReady}
                      rowData={loanRows}
                      suppressDragLeaveHidesColumns={true}
                      suppressMovableColumns={true}
                      enableRangeSelection={true}
                      animateRows={true}
                      rowHeight={32}
                      headerHeight={32}
                      onCellValueChanged={onCellValueChanged}
                      suppressLastEmptyLineOnPaste={true}
                      columnDefs={[
                        {
                          headerName: 'Identifier',
                          field: 'debtType',
                          cellClass: 'align-left',
                        },
                        { headerName: 'Line Item', field: 'name', editable: true },
                        { headerName: 'Spread', field: 'spread', editable: true },
                        { headerName: 'Maturity', field: 'maturity', editable: true },
                        { headerName: 'Agent', field: 'agent', editable: true },
                        {
                          headerName: 'Cov Light',
                          field: 'covenantLite',
                          editable: true,
                          cellEditor: 'agSelectCellEditor',
                          cellEditorParams: { values: ['NM', 'Yes', 'No', 'Springing'] },
                        },
                      ]}></AgGridReact>
                  </div>
                </div>
              </div>

              <BixButton type="button" disabled={!Bixby.Utils.isDefined(draftCompany)} onClick={handleSaveDraft}>
                Save as Draft
              </BixButton>
            </div>
          )}
          {!isEditing && (
            <div className="loan-details-data-grid">
              <div className="grid_container">
                <div className="grid ag-theme-alpine">
                  <AgGridReact
                    getContextMenuItems={getContextMenuItems}
                    defaultColDef={{
                      flex: 1,
                      minWidth: 100,
                      sortable: true,
                      resizable: true,
                      lockPinned: true,
                      cellClass: 'align-center',
                    }}
                    onGridReady={onGridReady}
                    rowData={loanRows}
                    suppressDragLeaveHidesColumns={true}
                    suppressMovableColumns={true}
                    enableRangeSelection={true}
                    animateRows={true}
                    rowHeight={32}
                    headerHeight={32}
                    onCellValueChanged={onCellValueChanged}
                    suppressLastEmptyLineOnPaste={true}
                    columnDefs={[
                      {
                        headerName: 'Identifier',
                        field: 'debtType',
                        cellClass: 'align-left',
                      },
                      { headerName: 'Line Item', field: 'name' },
                      { headerName: 'Spread', field: 'spread' },
                      { headerName: 'Maturity', field: 'maturity' },
                      { headerName: 'Agent', field: 'agent' },
                      {
                        headerName: 'Cov Light',
                        field: 'covenantLite',
                      },
                    ]}></AgGridReact>
                </div>
              </div>
            </div>
          )}
        </section>
      </React.Fragment>
    </div>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    isLoading: state.draftState.isLoading,
    error: state.draftState.error,
    draftCompany: state.draftState.draftCompany,
    isEditing: state.draftState.isEditing,
    discardDraft: state.draftState.discardDraft,
    currentCompany: state.companyState.currentCompany,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchUpdateDraftCompany: (companyId: string, draftCompanyCommand: Bixby.Models.JsonPatchOperation[]) =>
    dispatch(updateDraft(companyId, draftCompanyCommand)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyLoanDetails);
